import { CircularProgress, IconButton, Stack, SvgIconTypeMap, Tooltip } from '@mui/material'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-premium'
import { BaseColumnConfig } from '../types'
import { baseColumnConfig } from './baseColumnConfig'

export type ActionsColumnConfig<T extends GridValidRowModel> = BaseColumnConfig<T, 'actions'> & {
  columnType: 'actions'
  actions: (row: T) => {
    Icon: OverridableComponent<SvgIconTypeMap>
    tooltipTitle: string
    handleClick: (row: T) => void
    disabled: boolean
    hidden?: boolean
    loading?: boolean
  }[]
}

export const getActionsColumn = <T extends GridValidRowModel>({
  columnType,
  style,
  ...config
}: ActionsColumnConfig<T>): GridColDef<T> => ({
  ...baseColumnConfig,
  ...style,
  ...config,
  sortable: false,
  hideable: false,
  renderCell: (params) => (
    <Stack
      sx={{ height: '100%', width: '100%' }}
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      {...(config?.getDataTestId ? { ['data-testid']: config.getDataTestId(params) } : {})}
    >
      {config.actions(params.row).map((action, index) => {
        const { Icon, tooltipTitle, handleClick, disabled, hidden, loading } = action
        const key = `${Icon.name}-${index}`

        if (hidden) return null
        if (loading) return <CircularProgress key={key} size={20} sx={{ color: 'fg.subtle' }} />

        return (
          <Stack key={key} sx={{ height: '100%' }} justifyContent="center">
            <Tooltip title={tooltipTitle} disableHoverListener={false}>
              <Stack>
                <IconButton disabled={disabled} onClick={() => handleClick(params.row)}>
                  <Icon />
                </IconButton>
              </Stack>
            </Tooltip>
          </Stack>
        )
      })}
    </Stack>
  ),
})
